import type { Component } from 'react';
import { useState } from 'react';

import BpkSelectableChip, {
  CHIP_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-chip';
import { withButtonAlignment } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import CalendarIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/calendar';
import {
  BpkBox,
  BpkSpacing,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkMobileScrollContainer from '@skyscanner-internal/backpack-web/bpk-component-mobile-scroll-container';

import { useMBDContext } from '../../common/MBD/useMBDContext';
import DealsList from '../DealsList/DealsList';

import type { DealsEvolvedProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/DealsEvolved';

const iconsMap: { [key: string]: typeof Component } = {
  calendar: withButtonAlignment(CalendarIcon),
};
const EMPTY_DEALS_TAB = { title: '0', deals: [] };

const DealsTabs = (props: DealsEvolvedProps) => {
  const {
    airline,
    countryViewUrl,
    dealsLimit,
    filteringTabDeals,
    isMobile,
    pageTypeOptions,
    priceBandStrings,
    priceBandsDisplayState,
    shouldShow3by2Formation,
    strings,
    trackingContext,
  } = props;

  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const componentName = `${rawComponentName}_tab`;

  const onToggleGroup = (index: number) => {
    if (index !== activeGroupIndex && index >= 0) {
      setActiveGroupIndex(index);
    }
  };

  const tab = filteringTabDeals?.[activeGroupIndex];
  const tabDeals = tab?.deals || [];

  const getTabTitle = (index: number) =>
    (filteringTabDeals?.[index] || EMPTY_DEALS_TAB).title;
  const makeSlug = (title: string) =>
    title.toLocaleLowerCase().replaceAll(/\s+/g, '-');

  const tabId = (index: number) =>
    `deals-evolved-tab-${makeSlug(getTabTitle(index))}`;
  const panelId = (index: number) =>
    `deals-evolved-panel-${makeSlug(getTabTitle(index))}`;

  const icon = (titleIcon: string | undefined): React.ReactNode | null => {
    if (titleIcon && iconsMap[titleIcon]) {
      const Icon = iconsMap[titleIcon];
      return <Icon />;
    }
    return null;
  };

  const hasTabs = Boolean(filteringTabDeals && filteringTabDeals.length >= 2);

  return (
    <BpkVessel as="div">
      {hasTabs && (
        <BpkBox marginBottom={BpkSpacing.LG}>
          <BpkMobileScrollContainer>
            <div role="tablist">
              {filteringTabDeals?.map(
                ({ tabName, title, titleIcon }, index) => (
                  <BpkBox
                    as="span"
                    key={title}
                    display="inline-block"
                    height="2rem"
                    verticalAlign="middle"
                    marginBlock={BpkSpacing.SM}
                    marginEnd={BpkSpacing.SM}
                    marginStart={index === 0 ? BpkSpacing.None : BpkSpacing.SM}
                  >
                    <BpkSelectableChip
                      onClick={() => onToggleGroup(index)}
                      accessibilityLabel={title}
                      selected={index === activeGroupIndex}
                      aria-selected={
                        index === activeGroupIndex ? 'true' : 'false'
                      }
                      role="tab"
                      type={CHIP_TYPES.default}
                      id={tabId(index)}
                      data-index={index}
                      aria-controls={panelId(index)}
                      leadingAccessoryView={icon(titleIcon)}
                      data-tracking-element-id={`${componentName}_${index}`}
                      data-tracking-common-params={buildMBDParam(
                        componentName,
                        {
                          price_bands_display_state: priceBandsDisplayState,
                          tab: tabName,
                        },
                      )}
                    >
                      {title}
                    </BpkSelectableChip>
                  </BpkBox>
                ),
              )}
            </div>
          </BpkMobileScrollContainer>
        </BpkBox>
      )}

      {hasTabs ? (
        filteringTabDeals?.map((tabDealsGroup, index) => (
          <div
            key={panelId(index)}
            id={panelId(index)}
            role="tabpanel"
            aria-labelledby={tabId(index)}
            hidden={index !== activeGroupIndex}
          >
            {index === activeGroupIndex && (
              <DealsList
                {...{
                  strings,
                  deals: tabDeals,
                  airline,
                  originCode: trackingContext.originCode,
                  destinationCode: trackingContext.destinationCode,
                  isMobile,
                  trackingContext,
                  pageTypeOptions,
                  priceBandStrings,
                  priceBandsDisplayState,
                  shouldShow3by2Formation,
                  countryViewUrl,
                  dealsLimit,
                  tabName: tab?.tabName,
                }}
              />
            )}
          </div>
        ))
      ) : (
        <div>
          <DealsList
            {...{
              strings,
              deals: tabDeals,
              airline,
              originCode: trackingContext.originCode,
              destinationCode: trackingContext.destinationCode,
              isMobile,
              trackingContext,
              pageTypeOptions,
              priceBandStrings,
              priceBandsDisplayState,
              shouldShow3by2Formation,
              countryViewUrl,
              dealsLimit,
              tabName: tab?.tabName,
            }}
          />
        </div>
      )}
    </BpkVessel>
  );
};

export default DealsTabs;
